import React, { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import {
  CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE,
  DEFAULT_CURRENCY,
  GENERIC_INFORMATION_DEFAULT_VALUE,
  getCountryOfOriginOption,
  getCountryOfOriginPayload,
  getMaterialLocationCountryOption,
  getMaterialLocationCountryPayload,
  getProductDetailsPayload,
  toQuoteProductOption
} from '../helper';
import css from './index.module.scss';
import EnquiryGenericInformation from '../generic-information';
import ConsolidationQuote from './components/consolidation';
import { Button } from '@components/base';
import {
  IFilterState,
  IQuoteFormState,
  IWeightChildOption,
  IWeightSystemOption
} from '@helpers/types/quotes';
import { CURRENCY, WEIGHT_SYSTEM } from '@helpers/constants';
import CostCalculator from '../cost-calculator';
import { yupResolver } from '@hookform/resolvers/yup';
import { consolidationQuoteValidation } from '@helpers/yup/add-quote.schema';

interface IConsolidationProps {
  quoteFormState: IQuoteFormState;
  setQuoteFormState: React.Dispatch<React.SetStateAction<IQuoteFormState>>;
  filterState: IFilterState;
  filterProductOptions: (event: any, param?: any) => Promise<void>;
  filterPorts: (event?: any, param?: any, fieldType?: string) => Promise<void>;
  data: any;
}

const InternationalConsolidation = (props: IConsolidationProps) => {
  const {
    quoteFormState,
    setQuoteFormState,
    filterState,
    filterProductOptions,
    filterPorts,
    data
  } = props;

  const { consolidation } = filterState;

  const {
    selectedWeightSystemChildOptions,
    selectedCurrency,
    productOptions,
    portOfLoadingOptions,
    portOfDischargeOptions,
    enquiryId,
    duplicateEnquiry,
    firstFormData
  } = quoteFormState;

  const form = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(consolidationQuoteValidation),
    defaultValues: {
      genericInformation: GENERIC_INFORMATION_DEFAULT_VALUE,
      quote: [CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE]
    }
  });

  const { control, handleSubmit, getValues, reset } = form;

  const {
    fields: fields,
    append: append,
    remove: remove
  } = useFieldArray({
    name: 'quote',
    control
  });

  const handleAddQuote = () => {
    append(CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE);
  };

  const handleDublicateQuote = (index: number) => {
    const quoteToDuplicate = form.getValues(`quote.${index}`);
    append({
      ...quoteToDuplicate,
      quoteIsExpanded: true
    });
    const updatedQuotes = form.getValues('quote').map((item: any, i: any, arr: any) => {
      if (i === arr.length - 1) {
        return { ...item, quoteIsExpanded: true };
      } else {
        return { ...item, quoteIsExpanded: false };
      }
    });
    form.setValue('quote', updatedQuotes);
  };

  const primaryUnitWatch = useWatch({
    control,
    name: 'genericInformation.primaryUnit'
  });

  const currency1Watch = useWatch({
    control,
    name: `genericInformation.currency1`
  });

  const fxRateWatch = useWatch({
    control,
    name: `genericInformation.fxRate`
  });

  const onFormSubmit = (data: any) => {
    const currentFormData = getValues();
    const reqBody = {
      currency_type_one: data.genericInformation.currency1?.value,
      currency_type_two: data.genericInformation.currency2?.value,
      fx_rate: data.genericInformation.fxRate,
      weight_system: data.genericInformation.weightSystem?.value,
      deal_name: data.genericInformation.draftDealName,
      remarks: data.genericInformation.remarks,
      primary_unit: data.genericInformation.primaryUnit?.value,
      quotes: (data.quote || []).map((quote: any) => {
        const incoterm = quote.incoTerms?.value;
        let polId = null;
        let podId = null;
        let dealIncotermLocation = null;

        if (incoterm === 'FOB') {
          polId = quote.portOfLoading?.value ?? null;
        } else if (['CIF', 'CNF', 'CFR'].includes(incoterm)) {
          podId = quote.portOfDischarge?.value ?? null;
        } else if (incoterm === 'EXW') {
          dealIncotermLocation = quote?.incoTermsLocation ?? null;
        } else if (['DDP', 'DAP'].includes(incoterm)) {
          dealIncotermLocation = quote?.incoTermsLocation ?? null;
        }

        return {
          products: (quote.product || []).map((product: any) => {
            return {
              product_details: getProductDetailsPayload(product?.productName),
              quantity: product.dealQuantity,
              quantity_unit: product.dealQuantityUnit.value,
              packaging: product.productPackaging,
              moq: product.moq,
              moq_unit: product.moqUnit?.value,
              profit_margin: product.profitMarginExpected,
              costs: {
                base_price: {
                  currency: product?.base_price?.currency?.value,
                  value: parseFloat(product.base_price?.value),
                  cost_id: product.base_price?.cost_id
                },
                ex_works_price: {
                  currency: product?.final_exWorks_price?.currency?.value,
                  value: parseFloat(product.final_exWorks_price?.value),
                  cost_id: product.final_exWorks_price?.cost_id
                },
                fob_price: {
                  currency: product.fob_price?.currency?.value,
                  value: parseFloat(product.fob_price?.value),
                  cost_id: product.fob_price?.cost_id
                },
                cfr_price: {
                  currency: product.cfr_price?.currency?.value,
                  value: parseFloat(product.cfr_price?.value),
                  cost_id: product.cfr_price?.cost_id
                },
                cif_price: {
                  currency: product.cif_price?.currency?.value,
                  value: parseFloat(product.cif_price?.value),
                  cost_id: product.cif_price?.cost_id
                },
                final_price: {
                  currency: data?.genericInformation.currency1?.value,
                  value: parseFloat(product.finalQuote_selling_price?.value),
                  cost_id: product.finalQuote_selling_price?.cost_id
                },
                additional_cost: {
                  currency: quote?.additionalCost?.currency?.value,
                  value: parseFloat(quote.additionalCost?.value),
                  cost_id: quote.additionalCost?.cost_id
                }
              },
              final_price_weight_unit: product.finalQuote_selling_price?.weightUnit?.value,
              origin: getCountryOfOriginPayload(product.countryOfOrigin),
              material_location_country: getMaterialLocationCountryPayload(
                product.materialLocationCountry
              )
            };
          }),
          common_costs: {
            total_local_freight: {
              currency: quote?.totalLocalFreight?.currency?.value,
              value: parseFloat(quote.totalLocalFreight?.value),
              cost_id: quote.totalLocalFreight?.cost_id
            },
            total_port_and_clearance_expenses: {
              currency: quote.totalPortAndClearanceExpenses?.currency?.value,
              value: parseFloat(quote.totalPortAndClearanceExpenses?.value),
              cost_id: quote.totalPortAndClearanceExpenses?.cost_id
            },
            total_freight: {
              currency: quote.totalFreight?.currency?.value,
              value: parseFloat(quote.totalFreight?.value),
              cost_id: quote.totalFreight?.cost_id
            },
            switch_bl_cost: {
              currency: quote.switchBlCost?.currency?.value,
              value: parseFloat(quote.switchBlCost?.value),
              cost_id: quote.switchBlCost?.cost_id
            }
          },
          total_weight: parseFloat(quote.totalWeight),
          total_weight_unit: quote.totalWeightUnit.value,
          pol_id: polId,
          pod_id: podId,
          insurance_cost: parseFloat(quote.insuranceCost),
          finance_cost: parseFloat(quote.financeCost),
          discount: parseFloat(quote.discount),
          deal_incoterm: quote.incoTerms?.value,
          deal_incoterm_location: dealIncotermLocation
        };
      })
    };
    setQuoteFormState((prevState: IQuoteFormState) => ({
      ...prevState,
      firstFormReqBody: reqBody,
      firstFormData: currentFormData
    }));
  };

  useEffect(() => {
    if (firstFormData) {
      form.reset(firstFormData);

      const selectedChildWeights = WEIGHT_SYSTEM.find(
        (item: IWeightSystemOption) =>
          item.value === firstFormData.genericInformation.weightSystem.value
      )?.childOptions;

      setQuoteFormState((prevState: IQuoteFormState) => ({
        ...prevState,
        selectedWeightSystemChildOptions: selectedChildWeights || [],
        selectedCurrency: [
          CURRENCY.find(
            (item) => item.value === firstFormData.genericInformation.currency1.value
          ) || DEFAULT_CURRENCY,
          CURRENCY.find(
            (item) => item.value === firstFormData.genericInformation.currency2.value
          ) || DEFAULT_CURRENCY
        ]
      }));
    } else if (data) {
      const selectedChildWeights = WEIGHT_SYSTEM.find(
        (item: IWeightSystemOption) => item.value === data.weight_system
      )?.childOptions;

      setQuoteFormState((prevState: IQuoteFormState) => ({
        ...prevState,
        selectedWeightSystemChildOptions: selectedChildWeights || [],
        selectedCurrency: [
          CURRENCY.find((item) => item.value === data.currency_type_one) || DEFAULT_CURRENCY,
          CURRENCY.find((item) => item.value === data.currency_type_two) || DEFAULT_CURRENCY
        ],
        enquiryId: data.enquiry_id
      }));

      const primaryCurrency = CURRENCY.find((item) => item.value === data.currency_type_one);
      const primaryUnit = selectedChildWeights?.find(
        (item: IWeightChildOption) => item.value === data?.primary_unit
      );
      const consolidationQuotes = (data.consolidations || [])
        .map((item: any, index: number) => {
          return item.quotes;
        })
        .flat();
      form.reset({
        genericInformation: {
          currency1: CURRENCY.find((item) => item.value === data?.currency_type_one),
          currency2: CURRENCY.find((item) => item.value === data?.currency_type_two),
          fxRate: data.fx_rate,
          weightSystem: WEIGHT_SYSTEM?.find(
            (item: IWeightSystemOption) => item.value === data?.weight_system
          ),
          draftDealName: data?.deal_name,
          remarks: data?.remarks,
          primaryUnit: selectedChildWeights?.find(
            (item: IWeightChildOption) => item.value === data?.primary_unit
          )
        },
        quote: (consolidationQuotes || []).map((quote: any) => {
          return {
            product: (quote.products || []).map((product: any) => {
              return {
                isExpanded: false,
                productName: toQuoteProductOption(product?.product_data),
                productPackaging: product?.packaging,
                dealQuantity: product?.quantity,
                dealQuantityUnit: product?.quantity_unit
                  ? selectedChildWeights?.find(
                      (item: IWeightChildOption) => item.value === product?.quantity_unit
                    )
                  : primaryUnit,
                profitMarginExpected: product?.profit_margin,
                base_price: {
                  value: product.base_price ? product.base_price?.value : 0,
                  currency: product?.base_price
                    ? CURRENCY.find((item) => item.value === product.base_price?.currency)
                    : primaryCurrency,
                  cost_id: duplicateEnquiry ? null : product.base_price?.cost_id
                },
                fob_price: {
                  value: product.fob_price ? product.fob_price?.value : 0,
                  currency: product.fob_price
                    ? CURRENCY.find((item) => item.value === product.fob_price?.currency)
                    : primaryCurrency,
                  cost_id: duplicateEnquiry ? null : product.fob_price?.cost_id,
                  manualOverride: true
                },
                cfr_price: {
                  value: product.cfr_price ? product.cfr_price?.value : 0,
                  currency: product.cfr_price
                    ? CURRENCY.find((item) => item.value === product.cfr_price?.currency)
                    : primaryCurrency,
                  cost_id: duplicateEnquiry ? null : product.cfr_price?.cost_id,
                  manualOverride: true
                },
                cif_price: {
                  value: product.cif_price ? product.cif_price?.value : 0,
                  currency: product.cif_price
                    ? CURRENCY.find((item) => item.value === product.cif_price?.currency)
                    : primaryCurrency,
                  cost_id: duplicateEnquiry ? null : product.cif_price?.cost_id,
                  manualOverride: true
                },
                final_exWorks_price: {
                  value: product.ex_works_price ? product.ex_works_price?.value : 0,
                  currency: product.ex_works_price
                    ? CURRENCY.find((item) => item.value === product.ex_works_price?.currency)
                    : primaryCurrency,
                  cost_id: duplicateEnquiry ? null : product.ex_works_price?.cost_id,
                  manualOverride: true
                },
                finalQuote_selling_price: {
                  value: product.final_price ? product.final_price?.value : 0,
                  currency: product.final_price
                    ? CURRENCY.find((item) => item.value === product.final_price?.currency)
                    : primaryCurrency,
                  cost_id: duplicateEnquiry ? null : product.final_price?.cost_id,
                  weightUnit: product?.final_price_weight_unit
                    ? selectedChildWeights?.find(
                        (item: IWeightChildOption) =>
                          item.value === product?.final_price_weight_unit
                      )
                    : primaryUnit,
                  manualOverride: true
                },
                moq: product?.moq,
                moqUnit: product?.moq_unit
                  ? selectedChildWeights?.find(
                      (item: IWeightChildOption) => item.value === product?.moq_unit
                    )
                  : primaryUnit,
                countryOfOrigin: getCountryOfOriginOption(
                  product?.origin,
                  product?.origin_display_value
                ),
                materialLocationCountry: getMaterialLocationCountryOption(
                  product?.material_location_country,
                  product?.material_location_country_display_value
                ),
                additionalCost: {
                  value: product.additional_cost ? product.additional_cost?.value : 0,
                  currency: product.additional_cost
                    ? CURRENCY.find((item) => item.value === product.additional_cost?.currency)
                    : primaryCurrency,
                  cost_id: duplicateEnquiry ? null : product.additional_cost?.cost_id
                }
              };
            }),
            quoteIsExpanded: true,
            totalWeight: quote?.total_weight,
            totalWeightUnit: quote.total_weight_unit
              ? selectedChildWeights?.find(
                  (item: IWeightChildOption) => item.value === quote.total_weight_unit
                )
              : primaryUnit,
            totalLocalFreight: {
              value: quote.total_local_freight ? quote.total_local_freight?.value : 0,
              currency: quote.total_local_freight
                ? CURRENCY.find((item) => item.value === quote.total_local_freight?.currency)
                : null,
              cost_id: duplicateEnquiry ? null : quote.total_local_freight?.cost_id
            },
            totalPortAndClearanceExpenses: {
              value: quote.total_port_and_clearance_expenses
                ? quote.total_port_and_clearance_expenses?.value
                : 0,
              currency: quote.total_port_and_clearance_expenses
                ? CURRENCY.find(
                    (item) => item.value === quote.total_port_and_clearance_expenses?.currency
                  )
                : null,
              cost_id: duplicateEnquiry ? null : quote.total_port_and_clearance_expenses?.cost_id
            },
            switchBlCost: {
              value: quote.switch_bl_cost ? quote.switch_bl_cost?.value : 0,
              currency: quote.switch_bl_cost
                ? CURRENCY.find((item) => item.value === quote.switch_bl_cost?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote.switch_bl_cost?.cost_id
            },
            totalFreight: {
              value: quote.total_freight ? quote.total_freight?.value : 0,
              currency: quote.total_freight
                ? CURRENCY.find((item) => item.value === quote.total_freight?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote.total_freight?.cost_id
            },
            portOfLoading: quote?.pol
              ? {
                  label: quote.pol?.name,
                  value: quote.pol?.id
                }
              : null,
            portOfDischarge: quote?.pod
              ? {
                  label: quote.pod?.name,
                  value: quote.pod?.id
                }
              : null,
            incoTermsLocation: quote?.deal_incoterm_location,
            insuranceCost: quote?.insurance_cost,
            financeCost: quote?.finance_cost,
            profitMarginExpected: quote?.profit_margin,
            discount: quote?.discount,
            incoTerms: quote?.deal_incoterm
              ? {
                  value: quote?.deal_incoterm,
                  label: quote?.deal_incoterm
                }
              : null
          };
        })
      });
    }
  }, [data]);

  const quoteWatch = useWatch({
    control,
    name: 'quote'
  });

  return (
    <>
      <FormProvider {...form}>
        <form noValidate onSubmit={handleSubmit(onFormSubmit)} className={css.mainWrapper}>
          <CostCalculator
            filters={filterState}
            weightOptions={selectedWeightSystemChildOptions}
            currency={currency1Watch}
            primaryUnitWatch={primaryUnitWatch}
            quote={quoteWatch}
          />
          <EnquiryGenericInformation
            setQuoteFormState={setQuoteFormState}
            selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
          />

          {fields.map((item: any, index: number) => {
            const handleDeleteQuoteClick = () => {
              remove(index);
            };

            return (
              <ConsolidationQuote
                key={index}
                quoteIndex={index}
                isEnquiry={true}
                handleQuoteDeleteClick={handleDeleteQuoteClick}
                handleDublicateQuote={handleDublicateQuote}
                product={`quote.${index}.product`}
                quoteIsExpanded={`quote.${index}.quoteIsExpanded`}
                totalWeight={`quote.${index}.totalWeight`}
                totalWeightUnit={`quote.${index}.totalWeightUnit`}
                totalLocalFreight={`quote.${index}.totalLocalFreight.value`}
                totalLocalFreightCurrency={`quote.${index}.totalLocalFreight.currency`}
                totalPortAndClearanceExpenses={`quote.${index}.totalPortAndClearanceExpenses.value`}
                totalPortAndClearanceExpensesCurrency={`quote.${index}.totalPortAndClearanceExpenses.currency`}
                totalFreight={`quote.${index}.totalFreight.value`}
                totalFreightCurrency={`quote.${index}.totalFreight.currency`}
                switchBlCost={`quote.${index}.switchBlCost.value`}
                switchBlCostCurrency={`quote.${index}.switchBlCost.currency`}
                portOfLoading={`quote.${index}.portOfLoading`}
                portOfDischarge={`quote.${index}.portOfDischarge`}
                insuranceCost={`quote.${index}.insuranceCost`}
                discount={`quote.${index}.discount`}
                financeCost={`quote.${index}.financeCost`}
                incoTerms={`quote.${index}.incoTerms`}
                incoTermsLocation={`quote.${index}.incoTermsLocation`}
                filterState={filterState}
                selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
                selectedCurrency={selectedCurrency}
                filterProductOptions={filterProductOptions}
                productOptions={productOptions}
                filterPorts={filterPorts}
                portOfLoadingOptions={portOfLoadingOptions}
                portOfDischargeOptions={portOfDischargeOptions}
                primaryUnitWatch={primaryUnitWatch}
                currency1Watch={currency1Watch}
                fxRateWatch={fxRateWatch}
                fieldLength={fields.length}
              />
            );
          })}
          <Button
            variant="outlined-secondary"
            onClick={() => handleAddQuote()}
            className={css.addQuoteButton}
          >
            {consolidation ? 'Add Quote' : 'Add Product'}
          </Button>
          <div className={css.actionButtons}>
            <Button variant="contained" type="submit">
              Save and Proceed
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default InternationalConsolidation;
